<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import { focusInputById } from "~/utils/input/focus"
import { LoginSignupInputProps } from "~/components/LoginSignup/Organism/LoginInput.props"
import { navigateToLoginSignupPage } from "~/utils/loginSignup/loginSignup"
import { useUserStore } from "~/stores/user"
import { LOGINSIGNUP } from "~/utils/constants"

const { sendEventErrorForm } = useGA4SiteEvents()
const props = withDefaults(defineProps<LoginSignupInputProps>(), {
  showPoliciesDisclaimer: true,
  defaultEmail: "",
  hasDisabledEmail: false,
  hasItemsWithDefaultWidhts: true
})

const emit =
  defineEmits<{
    (e: "loginSuccess"): void
    (e: "onEdit"): void
    (e: "onForgotPassword"): void
  }>()

// Use custom composable for modal that emits events instead of reloading
const useModalLoginRegistration = () => {
  const userStore = useUserStore()

  const { authToken, refreshToken, commerceLayerToken } = {
    authToken: useStatefulCookie("authToken", { maxAge: 86400 }),
    refreshToken: useStatefulCookie("refreshToken", { maxAge: 2592000 }),
    commerceLayerToken: useStatefulCookie("cmlToken", { maxAge: 3600 })
  }
  const errorMessage = ref<string | undefined>(undefined)

  const handleAuth = async (authPromise: Promise<any>) => {
    try {
      errorMessage.value = undefined
      const response = await authPromise
      if (typeof response === "string") {
        if (response === LOGINSIGNUP.resetPassword) {
          errorMessage.value = LOGINSIGNUP.resetPassword
        } else {
          errorMessage.value = response
        }
      } else {
        const {
          authToken: token,
          refreshToken: rToken,
          commerceLayerToken: clToken
        } = response
        authToken.value = token
        refreshToken.value = rToken
        commerceLayerToken.value = clToken
        errorMessage.value = undefined

        // Emit event instead of reloading
        emit("loginSuccess")
      }
    } catch (error) {
      errorMessage.value = t("loginSignup.loginPage.errorMessage")
      console.warn("ERROR: ", error)
    }
  }

  const handleLogin = (fields: { email: string; password: string }) => {
    handleAuth(userStore.login(fields))
  }

  return {
    errorMessage,
    handleLogin
  }
}

const { handleLogin, errorMessage } = useModalLoginRegistration()
const { isEmailInputFocused, isPasswordInputFocused, focusBothInputs } =
  useChromeDoubleFocus()
const { t } = useI18n()

const emailField = ref(props.defaultEmail || "")
const hasDisabledEmail = ref(props.hasDisabledEmail)
const passwordField = ref("")
const isLoading = ref(false)
const showErrorMessage = ref(true)
const hasSentEmail = ref(false)
const itemsWidthsClass = [
  props.hasItemsWithDefaultWidhts ? "md:w-[344px] lg:w-[424px]" : "w-full"
]

watch(errorMessage, (newValueError) => {
  if (isLoading.value && newValueError !== "") {
    isLoading.value = false
  }
})

const handleSubmit = (fields: {
  email: string
  password: string
  subscribeNewsletter: boolean
}) => {
  errorMessage.value = ""
  isLoading.value = true
  showErrorMessage.value = true

  handleLogin(fields)
  focusInputById("login-password")
}

const isResetPassword = computed(
  () => errorMessage.value === LOGINSIGNUP.resetPassword
)
const sendLinkResetPassword = async () => {
  if (!emailField.value) {
    return
  }

  const { data, error } = await useFetch(
    `/api/account/link-reset-password/${encodeURIComponent(emailField.value)}`
  )
  if (data.value) {
    hasSentEmail.value = true
  }
  if (error.value) {
    errorMessage.value = t(
      "loginSignup.loginPage.linkResetPassword.emailSendError"
    )
  }
}

const handleSubmitInvalid = () => {
  sendEventErrorForm("loginForm")
  focusInputById("login-password")
}

const handleEditEmail = () => {
  emit("onEdit")
}

const handleForgotPassword = () => {
  emit("onForgotPassword")
}
</script>

<template>
  <div class="flex w-full flex-col gap-4">
    
<FormKitLazyProvider config-file="true">
<FormKit
      type="form"
      id="loginForm"
      :actions="false"
      form-class="flex w-full flex-col gap-4 md:items-center"
      :incomplete-message="false"
      @submit="handleSubmit"
      @submit-invalid="handleSubmitInvalid"
    >
      <TextfieldsEmail
        name="email"
        v-model="emailField"
        :loqate-check="false"
        :class="itemsWidthsClass"
        :label="$t('loginSignup.emailField')"
        :force-focus="focusBothInputs"
        :disabled="hasDisabledEmail"
        @on-focus-in="isEmailInputFocused = true"
        @on-focus-out="isEmailInputFocused = false"
        @on-edit="handleEditEmail"
      />
      <TextfieldsInputText
        v-model="passwordField"
        id="login-password"
        type="password"
        name="password"
        is-required
        validation="required"
        :class="itemsWidthsClass"
        :label="
          $t('dialogsAndModals.organism.loginAndRegistration.passwordField')
        "
        :validation-messages="{
          required: $t('textfields.passwordCheck.passwordRequired')
        }"
        :force-focus="focusBothInputs"
        @on-focus-in="isPasswordInputFocused = true"
        @on-focus-out="isPasswordInputFocused = false"
      />

      <div
        class="
          mouse-medium
          text-green-600
          hover:text-green-700
          block
          cursor-pointer
          text-right
          underline
        "
        :class="itemsWidthsClass"
        @click="handleForgotPassword"
      >
        {{
          $t(
            "dialogsAndModals.organism.loginAndRegistration.forgetPasswordText"
          )
        }}
      </div>
      <div v-if="!isResetPassword">
        <UtilsSimpleErrorParagraph
          v-if="errorMessage && showErrorMessage"
          :message="errorMessage"
        ></UtilsSimpleErrorParagraph>
      </div>
      <div class="py-2" v-else>
        <div v-if="!hasSentEmail">
          <UtilsSimpleErrorParagraph
            :message="
              $t(
                'loginSignup.loginPage.linkResetPassword.passwordNotValidError'
              )
            "
          ></UtilsSimpleErrorParagraph>
          <p
            @click="sendLinkResetPassword"
            class="mouse text-negative-main cursor-pointer"
          >
            {{
              $t(
                "loginSignup.loginPage.linkResetPassword.clickHereToAskNewPassword"
              )
            }}
          </p>
        </div>
        <UtilsSimpleErrorParagraph
          v-else
          message="$t('loginSignup.loginPage.linkResetPassword.emailSentMessage')"
        ></UtilsSimpleErrorParagraph>
      </div>

      <UtilsButton
        type="submit"
        class="h-10 w-full"
        :text="$t('dialogsAndModals.organism.loginAndRegistration.enter')"
        :is-loading="isLoading"
      >
        <template #icon>
          <UtilsLoader v-if="isLoading" class="mr-2" />
        </template>
      </UtilsButton>
    </FormKit>
</FormKitLazyProvider>

  </div>
</template>
